import { addDatadogRumAction, isDatadogRumInitialized } from '@/utils/datadog';
import { shouldBlockTracking, trackEvent } from '@/utils/mixpanel';

export const ANALYTICS_EVENTS = Object.freeze({
  PAGE_VIEWED: 'Page Viewed',
  NAVIGATION_CLICKED: 'Navigation Clicked',
  SCROLL_DEPTH_REACHED: 'Scroll Depth Reached',
  OUTBOUND_CLICK: 'Outbound Click',
  AUTH_STARTED: 'Auth Started',
  REGISTRATION_STARTED: 'Registration Started',
  REGISTRATION_STEP_VIEWED: 'Registration Step Viewed',
  REGISTRATION_COMPLETED: 'Registration Completed',
  CONTENT_CLICKED: 'Content Clicked',
  LIBRARY_SEARCH_PERFORMED: 'Library Search Performed',
  SERMON_OPENED: 'Sermon Opened',
  SERMON_PLAYBACK_STARTED: 'Sermon Playback Started',
  SERMON_PLAYBACK_PROGRESSED: 'Sermon Playback Progressed',
  SERMON_PLAYBACK_COMPLETED: 'Sermon Playback Completed',
  SERVICE_REPLAY_OPENED: 'Service Replay Opened',
  SERVICE_REPLAY_PLAYED: 'Service Replay Played',
  SERVICE_REPLAY_SELECTED: 'Service Replay Selected',
  EVENT_VIEWED: 'Event Viewed',
  EVENT_REGISTER_CLICKED: 'Event Register Clicked',
  EVENT_FILTER_APPLIED: 'Event Filter Applied',
  UPDATE_VIEWED: 'Update Viewed',
  UPDATE_FILTER_APPLIED: 'Update Filter Applied',
  BF_QUIZ_STARTED: 'BF Quiz Started',
  BF_QUIZ_ANSWERED: 'BF Quiz Answered',
  BF_QUIZ_COMPLETED: 'BF Quiz Completed',
  BMT_SIGNUP_SUBMITTED: 'BMT Sign-up Submitted',
  BMT_HOMEWORK_SUBMITTED: 'BMT Homework Submitted',
});

const SENSITIVE_PROPERTY_KEYS = new Set([
  'amount',
  'bankaccount',
  'bankaccountnumber',
  'maskedbankaccnumber',
  'accountnumber',
  'cardnumber',
  'cvv',
  'email',
  'fullnric',
  'icnumber',
  'mobile',
  'name',
  'nric',
  'partialnric',
  'password',
  'personalid',
  'phone',
  'pin',
  'referencenumber',
  'token',
]);

const hasValue = value => value !== null && value !== undefined && value !== '';

const recentAnalyticsEvents = new Map();

const getCurrentPath = () => {
  if (typeof window === 'undefined') return undefined;
  return window.location?.pathname;
};

const getRoutePath = route => {
  if (!route) return undefined;
  if (typeof route === 'string') return route;
  return route.path;
};

const isUnmatchedRouterRoute = route =>
  route && typeof route === 'object' && Array.isArray(route.matched) && route.matched.length === 0;

export const shouldTrackPageRoute = route => {
  const routePath = getRoutePath(route);
  if (!routePath) return false;
  if (isUnmatchedRouterRoute(route)) return false;

  return true;
};

const getRouteGroup = path => {
  if (!path || path === '/') return 'root';

  const segments = path.split('/').filter(Boolean);
  if (segments[0] === 'tabs') return segments[1] || 'tabs';

  return segments[0] || 'root';
};

const normalizePropertyKey = key =>
  String(key)
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();

const isSensitiveProperty = key => SENSITIVE_PROPERTY_KEYS.has(normalizePropertyKey(key));

const isDuplicateEvent = (dedupeKey, dedupeWindowMs) => {
  if (!dedupeKey || !dedupeWindowMs) return false;

  const now = Date.now();
  const previousAt = recentAnalyticsEvents.get(dedupeKey);
  recentAnalyticsEvents.set(dedupeKey, now);

  return previousAt !== undefined && now - previousAt < dedupeWindowMs;
};

export const getSafeOutboundDestination = url => {
  if (typeof url !== 'string' || !url.trim()) return undefined;

  const rawUrl = url.trim();

  try {
    const base = typeof window !== 'undefined' ? window.location?.origin : undefined;
    const parsedUrl = new URL(rawUrl, base || 'https://hogc-one.local');

    if (!/^https?:$/.test(parsedUrl.protocol)) return undefined;
    if (base && parsedUrl.origin === base && rawUrl.startsWith('/')) return parsedUrl.pathname;

    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch (error) {
    return rawUrl.split(/[?#]/)[0];
  }
};

export const sanitizeAnalyticsProperties = properties => {
  return Object.entries(properties || {}).reduce((sanitized, [key, value]) => {
    if (!hasValue(value) || isSensitiveProperty(key)) return sanitized;

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      sanitized[key] = value;
    }

    return sanitized;
  }, {});
};

export const trackAnalyticsEvent = (eventName, properties = {}, options = {}) => {
  const routePath = options.routePath || properties.route_path || getCurrentPath();

  if (options.blockSensitiveRoutes !== false && shouldBlockTracking(routePath)) {
    console.log(`[Analytics Blocked] Tracking disabled for sensitive route: ${eventName}`);
    return false;
  }

  if (isDuplicateEvent(options.dedupeKey, options.dedupeWindowMs)) {
    console.log(`[Analytics Deduped] Skipped duplicate event: ${eventName}`);
    return false;
  }

  const eventProperties = sanitizeAnalyticsProperties({
    ...properties,
    app_env: process.env.VUE_APP_NODE_ENV || process.env.NODE_ENV || 'development',
    tracking_source: properties.tracking_source || options.source || 'hogc-one-mobile',
  });

  trackEvent(eventName, eventProperties);

  if (isDatadogRumInitialized()) {
    addDatadogRumAction(eventName, eventProperties);
  }

  return true;
};

export const trackPageView = (to, from) => {
  if (!shouldTrackPageRoute(to)) return false;

  const routePath = getRoutePath(to);
  // A blocked route must not leak through the next page's view either: its path can carry
  // a bearer code (/courses/redeem/<code>) or say something about the member
  // (/agm/briefing). The group stays, which names only the section.
  const fromPath = getRoutePath(from);
  const previousRoutePath = shouldBlockTracking(fromPath) ? undefined : fromPath;

  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.PAGE_VIEWED,
    {
      route_name: typeof to?.name === 'string' ? to.name : undefined,
      route_path: routePath,
      route_group: getRouteGroup(routePath),
      previous_route_path: previousRoutePath,
      previous_route_group: getRouteGroup(fromPath),
      tracking_source: 'router',
    },
    { routePath },
  );
};

export const trackNavigationClick = ({
  label,
  page,
  destination,
  source,
  feature,
  routePath,
  object = 'navigation',
} = {}) => {
  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.NAVIGATION_CLICKED,
    {
      navigation_label: label,
      page,
      destination,
      feature,
      object,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'navigation',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackScrollDepth = ({ page, scrollPercentage, routePath, source } = {}) => {
  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.SCROLL_DEPTH_REACHED,
    {
      page,
      scroll_percentage: scrollPercentage,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'scroll',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackAuthStarted = ({ provider = 'auth0', source, routePath } = {}) => {
  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.AUTH_STARTED,
    {
      provider,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'auth',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackRegistrationStarted = ({ flow = 'public', source, routePath } = {}) => {
  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.REGISTRATION_STARTED,
    {
      flow,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'registration',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackRegistrationStepViewed = ({
  flow,
  step,
  stepName,
  stepNumber,
  stepTotal,
  source,
  routePath,
} = {}) => {
  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.REGISTRATION_STEP_VIEWED,
    {
      flow,
      step,
      step_name: stepName,
      step_number: stepNumber,
      step_total: stepTotal,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'registration',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackRegistrationCompleted = ({ flow, source, routePath } = {}) => {
  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.REGISTRATION_COMPLETED,
    {
      flow,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'registration',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackContentClick = ({
  contentType,
  contentId,
  title,
  sourceSection,
  position,
  destination,
  isExternal,
  source,
  routePath,
} = {}) => {
  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.CONTENT_CLICKED,
    {
      feature: 'content',
      content_type: contentType,
      content_id: contentId,
      title,
      source_section: sourceSection,
      position,
      destination,
      is_external: isExternal,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'content',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackLibrarySearch = ({ searchTerm, resultCount, source, routePath } = {}) => {
  const normalizedSearchTerm = typeof searchTerm === 'string' ? searchTerm.trim() : '';

  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.LIBRARY_SEARCH_PERFORMED,
    {
      feature: 'library',
      search_term: normalizedSearchTerm,
      search_length: normalizedSearchTerm.length,
      result_count: resultCount,
      has_results: typeof resultCount === 'number' ? resultCount > 0 : undefined,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'library-search',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackSermonOpened = ({ slug, title, source, routePath } = {}) => {
  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.SERMON_OPENED,
    {
      feature: 'library',
      content_type: 'sermon',
      content_id: slug,
      title,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'library',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackSermonPlayback = ({
  action,
  sermonId,
  title,
  progressPercentage,
  progressPoint,
  duration,
  timeWatched,
  source,
  routePath,
} = {}) => {
  const eventName =
    {
      started: ANALYTICS_EVENTS.SERMON_PLAYBACK_STARTED,
      progressed: ANALYTICS_EVENTS.SERMON_PLAYBACK_PROGRESSED,
      completed: ANALYTICS_EVENTS.SERMON_PLAYBACK_COMPLETED,
    }[action] || ANALYTICS_EVENTS.SERMON_PLAYBACK_PROGRESSED;

  return trackAnalyticsEvent(
    eventName,
    {
      feature: 'library',
      content_type: 'sermon',
      content_id: sermonId,
      title,
      progress_percentage: progressPercentage,
      progress_point: progressPoint,
      duration,
      time_watched: timeWatched,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'sermon-player',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackServiceReplayOpened = ({
  replayId,
  title,
  weekendId,
  weekendLabel,
  source,
} = {}) => {
  return trackAnalyticsEvent(ANALYTICS_EVENTS.SERVICE_REPLAY_OPENED, {
    feature: 'service-replay',
    content_type: 'service_replay',
    content_id: replayId,
    title,
    weekend_id: weekendId,
    weekend_label: weekendLabel,
    route_path: getCurrentPath(),
    tracking_source: source || 'service-replay',
  });
};

export const trackServiceReplaySelected = ({
  replayId,
  title,
  weekendId,
  weekendLabel,
  selectionType,
  source,
} = {}) => {
  return trackAnalyticsEvent(ANALYTICS_EVENTS.SERVICE_REPLAY_SELECTED, {
    feature: 'service-replay',
    content_type: 'service_replay',
    content_id: replayId,
    title,
    weekend_id: weekendId,
    weekend_label: weekendLabel,
    selection_type: selectionType,
    route_path: getCurrentPath(),
    tracking_source: source || 'service-replay',
  });
};

export const trackServiceReplayPlayed = ({
  replayId,
  title,
  weekendId,
  weekendLabel,
  source,
} = {}) => {
  return trackAnalyticsEvent(ANALYTICS_EVENTS.SERVICE_REPLAY_PLAYED, {
    feature: 'service-replay',
    content_type: 'service_replay',
    content_id: replayId,
    title,
    weekend_id: weekendId,
    weekend_label: weekendLabel,
    route_path: getCurrentPath(),
    tracking_source: source || 'service-replay-player',
  });
};

export const trackEventViewed = ({ eventId, slug, title, source, routePath } = {}) => {
  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.EVENT_VIEWED,
    {
      feature: 'events',
      content_type: 'event',
      content_id: eventId || slug,
      event_id: eventId,
      event_slug: slug || (typeof eventId === 'string' ? eventId : undefined),
      slug,
      title,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'events',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackEventRegisterClicked = ({ eventId, slug, title, destination, source } = {}) => {
  return trackAnalyticsEvent(ANALYTICS_EVENTS.EVENT_REGISTER_CLICKED, {
    feature: 'events',
    content_type: 'event',
    content_id: eventId || slug,
    event_id: eventId,
    event_slug: slug || (typeof eventId === 'string' ? eventId : undefined),
    slug,
    title,
    destination,
    route_path: getCurrentPath(),
    tracking_source: source || 'event-register',
  });
};

export const trackEventFilterApplied = ({ filter, source, routePath } = {}) => {
  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.EVENT_FILTER_APPLIED,
    {
      feature: 'events',
      filter,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'events',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackUpdateViewed = ({
  updateId,
  slug,
  title,
  updateType,
  source,
  routePath,
  redactTitle = false,
} = {}) => {
  const updateIdentifier = updateId || slug;

  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.UPDATE_VIEWED,
    {
      feature: 'updates',
      content_type: 'update',
      content_id: updateIdentifier,
      update_id: updateIdentifier,
      update_slug: slug,
      title: redactTitle ? undefined : title,
      update_type: updateType,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'updates',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

export const trackUpdateFilterApplied = ({ filter, source, routePath } = {}) => {
  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.UPDATE_FILTER_APPLIED,
    {
      feature: 'updates',
      filter,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'updates',
    },
    {
      routePath: routePath || getCurrentPath(),
      dedupeKey: `update-filter:${routePath || getCurrentPath()}:${filter}`,
      dedupeWindowMs: 1000,
    },
  );
};

export const trackOutboundClick = ({
  url,
  linkType,
  title,
  source,
  page,
  routePath,
  destination,
} = {}) => {
  const safeDestination = getSafeOutboundDestination(destination || url);

  return trackAnalyticsEvent(
    ANALYTICS_EVENTS.OUTBOUND_CLICK,
    {
      url: safeDestination,
      outbound_url: safeDestination,
      destination: safeDestination,
      link_type: linkType,
      title,
      page,
      route_path: routePath || getCurrentPath(),
      tracking_source: source || 'outbound-link',
    },
    { routePath: routePath || getCurrentPath() },
  );
};

// BMT Companion. Counts only: never the trainee's answer text or CG note, and
// never which programme (BMT Youth would tie an identified member to a youth
// programme, which HOGC treats as highly confidential).
export const trackBmtSignUp = ({ askedAboutCg, source = 'bmt-hub' } = {}) =>
  trackAnalyticsEvent(ANALYTICS_EVENTS.BMT_SIGNUP_SUBMITTED, {
    asked_about_cg: askedAboutCg,
    tracking_source: source,
  });

export const trackBmtHomeworkSubmitted = ({ homeworkType, done, source = 'bmt-hub' } = {}) =>
  trackAnalyticsEvent(ANALYTICS_EVENTS.BMT_HOMEWORK_SUBMITTED, {
    homework_type: homeworkType,
    done,
    tracking_source: source,
  });
